import moment from 'moment/moment'
import { InvoicePaymentType, routes } from '@linqpal/models'
import { currencyMask, dateMask } from '../../../../utils/helpers/masking'
import { TFunction } from 'react-i18next'
import { BaseTableStore } from '../../../../store/BaseTableStore'
import {
  IPayable,
  payableInvoiceStatuses,
} from '@linqpal/models/src/dictionaries'
import { getSalesDocumentStatusUIOptions } from '../../../../ui/molecules/StatusLabel/options/getSalesDocumentStatusUIOptions'
import { CustomerAccountType } from '@linqpal/models/src/dictionaries/customerAccountType'
import { PaidInvoiceStatuses, PaymentProcessingInvoiceStatuses } from '../enums'

export interface IPayablesInvoicesFilter {
  search?: string
  dateFrom?: string
  dateTo?: string
  dateFromIndex?: number
  statuses?: string[]
}

export interface IInvoiceTableItem {
  _id: string
  invoiceNumber: string
  totalAmount: number
  customerFee: string
  lateFee: string
  totalAmountDue: string
  dueDate: string
  paymentDate: string
  status: string
  supplierId: string
  supplierPhone: string
  supplierName: string
  selected: boolean
  isPayable: boolean
  totalPaidAmount: number
  totalProcessingAmount: number
  totalRemainingAmount: number
  paymentType: string | null
  payerId?: string
  invoice: IPayable // a reference to the raw invoice object
}

export class PayablesInvoicesStore extends BaseTableStore<
  IInvoiceTableItem,
  IPayablesInvoicesFilter
> {
  exportFileName = 'invoices_payables'

  vendorId: string | null = null

  vendorIsInvited = false

  isFactoring = false

  hasPayableInvoice = false

  totalSelectedCount = 0

  totalAmount = 0

  allSelected = false

  filter: IPayablesInvoicesFilter = {
    search: '',
    dateFrom: moment().subtract(30, 'days').format('MM/DD/YYYY'),
    dateTo: moment().format('MM/DD/YYYY'),
    dateFromIndex: 0,
    statuses: [''],
  }

  resetFilter() {
    this.filter = {
      search: '',
      dateFrom: moment().subtract(30, 'days').format('MM/DD/YYYY'),
      dateTo: moment().format('MM/DD/YYYY'),
      dateFromIndex: 0,
      statuses: [''],
    }
    this.resetPage()
    this.resetSort()
  }

  setVendor(id: string, isInvited: boolean) {
    this.vendorId = id
    this.vendorIsInvited = isInvited
    this.hasPayableInvoice = false
  }

  setIsFactoring(isFactoring: boolean) {
    this.isFactoring = isFactoring
  }

  setTotalSelectedCount(count) {
    this.totalSelectedCount = count
  }

  setSelectedTotalAmount(amount: number) {
    this.totalAmount = amount
  }

  setAllSelected(selected) {
    this.allSelected = selected
  }

  resetSelection() {
    this.totalSelectedCount = 0
    this.totalAmount = 0
    this.allSelected = false
    this.hasPayableInvoice = false
  }

  t: TFunction<'payables'> | undefined

  constructor() {
    super()
    this.initObservable()
  }

  async callApi(params: any): Promise<any> {
    this.resetSelection()

    const res = await routes.invoices.payablesInvoices({
      supplierId: this.vendorId,
      supplierIsInvited: this.vendorIsInvited || undefined,
      ...params,
    })
    return res
  }

  formatTableItem(invoice: IPayable) {
    const isPayable =
      payableInvoiceStatuses.includes(invoice.invoiceStatus) &&
      invoice.paymentType !== InvoicePaymentType.FACTORING &&
      invoice.customerType !== CustomerAccountType.IHC &&
      invoice.allowedForGroupPayment &&
      !!invoice.totalRemainingAmount

    if (isPayable) this.hasPayableInvoice = true

    return {
      _id: invoice._id,
      invoiceNumber: invoice.invoice_number,
      totalAmount: invoice.total_amount,
      customerFee: invoice.customerFee
        ? currencyMask(invoice.customerFee)
        : '-',
      totalAmountDue: currencyMask(invoice.totalAmountDue),
      lateFee: invoice.lateFee ? currencyMask(invoice.lateFee) : '-',
      dueDate: dateMask(invoice.invoice_due_date),
      paymentDate: invoice.payment_date ? dateMask(invoice.payment_date) : '-',
      status: invoice.invoiceStatus,
      supplierId: invoice.company?._id || '-',
      supplierPhone: invoice.company?.phone || '-',
      supplierName: invoice.company?.name || '-',
      selected: false,
      isPayable,
      totalPaidAmount: invoice.totalPaidAmount ?? 0,
      totalProcessingAmount: invoice.totalProcessingAmount ?? 0,
      totalRemainingAmount: invoice.totalRemainingAmount ?? 0,
      paymentType: invoice.paymentType,
      payerId: invoice.payer_id,
      invoice: invoice,
    }
  }

  formatExportItem(tableItem: IInvoiceTableItem): { [key: string]: string } {
    if (!this.t) {
      throw new Error('call setTranslation() first')
    }
    const paidAmount = tableItem.totalPaidAmount
      ? tableItem.totalPaidAmount
      : PaidInvoiceStatuses.includes(tableItem.status)
      ? tableItem.totalAmount
      : 0

    const processingAmount = tableItem.totalProcessingAmount
      ? tableItem.totalProcessingAmount
      : PaymentProcessingInvoiceStatuses.includes(tableItem.status)
      ? tableItem.totalAmount
      : 0

    const remainingAmount =
      PaymentProcessingInvoiceStatuses.includes(tableItem.status) ||
      PaidInvoiceStatuses.includes(tableItem.status)
        ? 0
        : tableItem.totalRemainingAmount

    return {
      [this.t('payables.tabInvoices.table.VendorName')]: tableItem.supplierName,
      [this.t('payables.tabInvoices.table.InvoiceNumber')]:
        tableItem.invoiceNumber,
      [this.t('payables.tabInvoices.table.DueDate')]: tableItem.dueDate,
      [this.t('payables.tabInvoices.table.Status')]:
        getSalesDocumentStatusUIOptions(tableItem.status).label,
      [this.t('payables.tabInvoices.table.Amount')]: currencyMask(
        tableItem.totalAmount,
      ),
      [this.t('payables.tabInvoices.table.CustomerFee')]: tableItem.customerFee,
      [this.t('payables.tabInvoices.table.LateFee')]: tableItem.lateFee,
      [this.t('payables.tabInvoices.table.PaidAmount')]:
        currencyMask(paidAmount),
      [this.t('payables.tabInvoices.table.ProcessingAmount')]:
        currencyMask(processingAmount),
      [this.t('payables.tabInvoices.table.RemainingAmount')]:
        currencyMask(remainingAmount),
    }
  }

  setTranslation(t: TFunction<'payables'>) {
    this.t = t
  }
}
